import { type PaletteOptions } from "@mui/material/styles";

export type MonotonePalette = {
  "0": string;
  50: string;
  200: string;
  300: string;
  400: string;
  500: string;
  700: string;
  800: string;
};

export type DefaultPalette = {
  subtle: string;
  strong: string;
  stronger: string;
};

export type StatusPalette = {
  warning: string;
  confirmation: string;
};

export type RubricPalette = {
  limited: string;
  emerging: string;
  expanding: string;
  bridging: string;
  advanced: string;
};

export type TextPalette = {
  primary: string;
  navigation: string;
  secondary: string;
  tertiary: string;
  inverted: string;
};

export type BackgroundPalette = {
  default: string;
  subtle: string;
};

export type BorderPalette = {
  default: string;
};

export type AppCustomPalette = {
  gray: MonotonePalette;
  purple: DefaultPalette;
  status: StatusPalette;
  rubric: RubricPalette;
  text: TextPalette;
  background: BackgroundPalette;
  border: BorderPalette;
};

interface CustomGrey {
  main: string;
  lighterGrey: string;
  opaquePrimary: string;
  lightGrey: string;
  adminButtonBorder: string;
  sgiLightGray: string;
  lightShedGrey: string;
  darkGrey: string;
  silver: string;
  iconGrade: string;
  dragBorder: string;
  buttonHover: string;
  buttonActive: string;
  lightVinyl: string;
  promptBackground: string;
  calendarGrey: string;
  submissionGrey: string;
  lineGrey: string;
}

interface CustomWhite {
  main: string;
  lightWhite: string;
  whiteSmoke: string;
  inputDisabled: string;
}

interface CustomBlue {
  main: string;
  activeGoalWritingVocabulary: string;
  aquaBlue: string;
  oldTheme: string;
  lightBlue: string;
  activeGoalVocabulary: string;
  primaryBlue: string;
  primaryLight: string;
  primaryDark: string;
  primaryHover: string;
  darkBlue: string;
  flashlightBlue: string;
  darkBlueTanned: string;
  darkMagenta: string;
  primaryPurple: string;
  cornflowerBlue: string;
  darkestBlue: string;
  filterColor: string;
  chipDark: string;
  darkIcon: string;
  waterBlue: string;
  fillBlue: string;
  purpleBright: string;
  toolTipIconBackground: string;
  purple: string;
  lightSky: string;
  activeGoalWritingGrammar: string;
}

export const customGrey: CustomGrey = {
  main: "#E8E8E8",
  lighterGrey: "#F8F8F8",
  opaquePrimary: "#DFF2FF",
  lightGrey: "#F9F9F9",
  adminButtonBorder: "#D1D5DB",
  sgiLightGray: "#AAAAAA",
  lightShedGrey: "#4D4D4D",
  darkGrey: "#9B9B9B",
  silver: "#C4C4C4",
  iconGrade: "#9CA3AF",
  dragBorder: "#E5E7EB",
  buttonHover: "#F0F2F5",
  buttonActive: "#E5E7EB",
  lightVinyl: "#C2E2F9",
  promptBackground: "#D8D8F3",
  calendarGrey: "#8D8D8D",
  submissionGrey: "#D9D9D9",
  lineGrey: "#CCCCCC",
};

export const customBlue: CustomBlue = {
  main: "#0000FF",
  activeGoalWritingVocabulary: "#B1C6FD",
  aquaBlue: "#0094FF",
  oldTheme: "#42B0FF",
  lightBlue: "#236DE3",
  activeGoalVocabulary: "#236696",
  primaryBlue: "#2A2A8B",
  primaryLight: "#3B3BC4",
  primaryDark: "#18184E",
  primaryHover: "#202071",
  darkBlue: "#1920A3",
  flashlightBlue: "#150C7E",
  darkBlueTanned: "#3C39FF",
  darkMagenta: "#0182E0",
  primaryPurple: "#3B17A0",
  cornflowerBlue: "#7270fe",
  darkestBlue: "#31405C",
  filterColor: "#374151",
  chipDark: "#6B7280",
  darkIcon: "#111827",
  waterBlue: "#0090F9",
  fillBlue: "#2a2a8b26",
  purpleBright: "#BF40BF",
  toolTipIconBackground: "#2A2A8B",
  purple: "#7270FE",
  lightSky: "#B2DFFF",
  activeGoalWritingGrammar: "#7a59d9",
};

export const customWhite: CustomWhite = {
  main: "#FFFFFF",
  lightWhite: "#fbfbfb",
  whiteSmoke: "#F7F7F7",
  inputDisabled: "#F4F4F4",
};

export const customPalette: AppCustomPalette = {
  gray: {
    0: "#FFFFFF",
    50: "#F7F7F7",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    700: "#4B5563",
    800: "#333333",
  },

  purple: {
    subtle: "#D8DBF3",
    strong: "#2A2A8B",
    stronger: "#18184E",
  },

  status: {
    warning: "#D12E2E",
    confirmation: "#16A34A",
  },

  rubric: {
    limited: "#F86A64",
    emerging: "#FBD467",
    expanding: "#2ECC57",
    bridging: "#3B82F6",
    advanced: "#7C70FE",
  },

  text: {
    primary: "#333333",
    navigation: "#4B5563",
    secondary: "#6B7280",
    tertiary: "#9CA3AF",
    inverted: "#FFFFFF",
  },

  background: {
    default: "#FFFFFF",
    subtle: "#F7F7F7",
  },

  border: {
    default: "#E5E7EB",
  },
};

export const muiPaletteOptions: PaletteOptions = {
  mode: "light",
  primary: {
    main: customPalette.purple.strong,
    dark: customPalette.purple.stronger,
    light: customPalette.purple.subtle,
    contrastText: customPalette.text.inverted,
  },
  error: {
    main: customPalette.status.warning,
  },
  success: {
    main: customPalette.status.confirmation,
  },
  text: {
    primary: customPalette.text.primary,
    secondary: customPalette.text.secondary,
    disabled: customPalette.text.tertiary,
  },
  background: {
    default: customPalette.background.default,
    paper: customPalette.background.subtle,
  },
  divider: customPalette.border.default,
  grey: {
    50: customPalette.gray[50],
    200: customPalette.gray[200],
    300: customPalette.gray[300],
    400: customPalette.gray[400],
    500: customPalette.gray[500],
    700: customPalette.gray[700],
    800: customPalette.gray[800],
  },
  custom: customPalette,
};
